import DeleteButton from "@components/Buttons/DeleteButton";
import OutlineButton from "@components/Buttons/OutlineButton";
import PrimaryButton from "@components/Buttons/PrimaryButton";
import { ChevronUpIcon, DropIcon, TrashIcon } from "@components/Icons";
import { ComponentHeader } from "@components/Tables/ComponentHeader";
import TableWithPagination from "@components/Tables/TableWithPagination";
import LicenseConflictModal from "@components/UI/LicenseConflictModal";
import PolicyConflictModal from "@components/UI/PolicyConflictModal";
import PreviewSelectionModal from "@components/UI/PreviewSelectionModal";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { getComponentLicenseConflict, getLicensePolicyResult, getLicensePolicyRule } from "@utils/common";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { COLOR_CONFLICT_FLAG } from "@constants/index";
import useItemsPerPage from "@hooks/useItemsPerPage";

const PreviewSection = ({
  title = "Preview Components List",
  className = "",
  projectPreview: projectPreviewProp = {},
  lisencePolicySeq: lisencePolicySeqProp,
  project,
  onProjectUpdated,
  showActionButtons = false,
  showSelectedComponent = true,
  showCheckbox = false,
  onDeleteComponents,
  onAddComponents,
  onRemoveSelectedComponent,
  refreshTrigger = 0,
  creatorList,
  "data-testid": dataTestId = "preview-section",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);
  const [previewComponents, setPreviewComponents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.PREVIEW_COMPONENT_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [licensePolicy, setLicensePolicy] = useState({});
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(projectPreviewProp || null);
  const [selectedLisencePolicySeq, setSelectedLisencePolicySeq] = useState(lisencePolicySeqProp);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isLicenseConflictModalOpen, setIsLicenseConflictModalOpen] = useState(false);
  const [isPolicyConflictModalOpen, setIsPolicyConflictModalOpen] = useState(false);
  const [selectedConflictData, setSelectedConflictData] = useState([]);
  const [selectedLicensePolicyResult, setSelectedLicensePolicyResult] = useState(null);
  const [hasCheckedPreviewExistence, setHasCheckedPreviewExistence] = useState(false);
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(true);
  const tid = (suffix) => `${dataTestId}-${suffix}`;

  // Safely handle useClientConfig hook to prevent crashes
  const clientConfigContext = useClientConfig();

  // Safe destructuring with null checks
  const clientConfig = clientConfigContext?.clientConfig || {};
  const clientConfigLoading = clientConfigContext?.isLoading || false;
  const clientConfigError = clientConfigContext?.error || null;
  const getClientConfigValue = clientConfigContext?.getClientConfigValue || (() => null);
  const isClientConfigEnabled = clientConfigContext?.isClientConfigEnabled || (() => false);

  useEffect(() => {
    if (projectPreviewProp && lisencePolicySeqProp) {
      setSelectedPreview(projectPreviewProp);
      setSelectedLisencePolicySeq(lisencePolicySeqProp);
      setHasCheckedPreviewExistence(false); // Reset check flag when preview changes
    }
  }, [projectPreviewProp, lisencePolicySeqProp]);

  // Update selectedPreview and related state when projectPreviewProp changes
  useEffect(() => {
    if (projectPreviewProp && projectPreviewProp.projectPreviewSeq) {
      setSelectedPreview(projectPreviewProp);
      if (projectPreviewProp.licensePolicySeq) {
        setSelectedLisencePolicySeq(projectPreviewProp.licensePolicySeq);
      }
      setHasCheckedPreviewExistence(false); // Reset check flag when preview changes
    } else {
      // If the parent no longer has a preview, immediately clear local selection/state
      setSelectedPreview(null);
      setSelectedLisencePolicySeq(null);
      setPreviewComponents([]);
      setTotalData(0);
      setTotalPages(0);
      setHasCheckedPreviewExistence(true); // Mark as checked since we have no preview
    }
  }, [projectPreviewProp?.projectPreviewSeq]);

  useEffect(() => {
    if (clientConfigContext && !clientConfigLoading && clientConfig && Object.keys(clientConfig).length > 0) {
      try {
        const usePreview = getClientConfigValue("usePreview");
        if (usePreview !== null && usePreview !== undefined) {
          setIsPreviewEnabled(usePreview === "Y");
        }
      } catch (error) {
        console.error("Error getting preference value:", error);
        // Fallback to default value
        setIsPreviewEnabled(true);
      }
    }
  }, [clientConfigContext, clientConfig, clientConfigLoading, getClientConfigValue]);

  // Keep UI in sync with backend project payload: if project has no projectPreview, clear selection
  useEffect(() => {
    if (project && !project.projectPreview) {
      setSelectedPreview(null);
      setIsExpanded(false);
      setSelectedLisencePolicySeq(null);
      setPreviewComponents([]);
      setTotalData(0);
      setTotalPages(0);
      setCurrentPage(1);
      setSortConfig({ key: null, direction: "asc" });
    }
  }, [project?.projectPreview]);

  const updateProjectPreviewLink = useMutationData(
    () => `${queryKeys.updateProjectPreviewLink}/${project?.projectSeq}/preview`,
    { method: "PUT" },
    {
      onSuccess: () => {
        setIsUpdatingProject(false);
        if (onProjectUpdated) onProjectUpdated();
        showSuccessToast(t("project.project-update-success"));
      },
      onError: (error) => {
        setIsUpdatingProject(false);
        console.error("Error updating project preview link:", error);
        showErrorToast(t("project.project-update-error"));
      },
    },
  );

  // Check if selected preview still exists when component mounts or selectedPreview changes
  const checkPreviewExists = useMutationData(
    (projectPreviewSeq) => `${queryKeys.getProjectPreview}/${projectPreviewSeq}`,
    { method: "GET" },
    {
      onSuccess: () => {
        // Preview exists, do nothing
        setHasCheckedPreviewExistence(true);
      },
      onError: () => {
        // Preview doesn't exist, clear selection
        console.warn("Selected preview no longer exists, clearing selection");
        setSelectedPreview(null);
        setSelectedLisencePolicySeq(null);
        setPreviewComponents([]);
        setTotalData(0);
        setTotalPages(0);
        setCurrentPage(1);
        setSortConfig({ key: null, direction: "asc" });
        setHasCheckedPreviewExistence(true);

        // If this is in ProjectDetail context, clear linked preview only
        if (project) {
          setIsUpdatingProject(true);
          updateProjectPreviewLink.mutate({ projectPreviewSeq: null });
        }
      },
    },
  );

  // Check preview existence when component mounts (only if we have a selected preview and haven't checked yet)
  useEffect(() => {
    if (selectedPreview?.projectPreviewSeq && !hasCheckedPreviewExistence && !checkPreviewExists.isPending && !projectPreviewProp.projectPreviewSeq) {
      checkPreviewExists.mutate(selectedPreview.projectPreviewSeq);
    }
  }, [selectedPreview?.projectPreviewSeq, hasCheckedPreviewExistence]); // Check when selectedPreview changes or when we haven't checked yet

  const getLicensePolicy = useMutationData(
    (lisencePolicySeq) => `${queryKeys.getLicensePolicyByLicenseId}/${lisencePolicySeq}`,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response && typeof response === "object") {
          setLicensePolicy(response);
        }
      },
      onError: (error) => {
        console.error("Error fetching license policy:", error);
        showErrorToast(t("common.get-license-policy-error"));
      },
    },
  );

  useEffect(() => {
    if (selectedLisencePolicySeq) {
      getLicensePolicy.mutate(selectedLisencePolicySeq);
    }
  }, [selectedLisencePolicySeq]);

  const getPreviewComponents = useMutationData(
    (params) => {
      const { projectPreviewSeq, pageSize, startRow } = params;
      return `${queryKeys.getProjectPreviewComponentByProjectPreviewSeqWithPagination}?projectPreviewSeq=${projectPreviewSeq}&pageSize=${pageSize}&startRow=${startRow}`;
    },
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response && typeof response === "object") {
          const data = response.object || [];
          const totalCount = response.objectCount || 0;

          // Calculate the new total pages based on the latest total count
          const newTotalPages = Math.ceil(totalCount / itemsPerPage) || 0;

          // Update totals immediately
          setTotalData(totalCount);
          setTotalPages(newTotalPages);

          // If current page is now out of bounds (e.g., deleted the last page),
          // move to the last available page to avoid showing an empty table
          if (newTotalPages > 0 && currentPage > newTotalPages) {
            setCurrentPage(newTotalPages);
            // Do not overwrite data here; a refetch will be triggered by currentPage change
          } else {
            setPreviewComponents(data);
          }
        } else {
          setPreviewComponents([]);
          setTotalData(0);
          setTotalPages(0);
        }
        setLoading(false);
      },
      onError: (error) => {
        setPreviewComponents([]);
        setTotalData(0);
        setTotalPages(0);
        setLoading(false);
        showErrorToast(t("project.project-preview.get-preview-components-error"));
      },
    },
  );

  useEffect(() => {
    // Always fetch from API to support pagination
    if (selectedPreview?.projectPreviewSeq) {
      setLoading(true);
      const startRow = (currentPage - 1) * itemsPerPage;
      getPreviewComponents.mutate({
        projectPreviewSeq: selectedPreview.projectPreviewSeq,
        pageSize: itemsPerPage,
        startRow,
      });
    } else {
      setPreviewComponents([]);
      setTotalData(0);
      setTotalPages(0);
      setLoading(false);
    }
  }, [selectedPreview?.projectPreviewSeq, currentPage, itemsPerPage, refreshTrigger]);

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const handleSort = (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === columnKey && sortConfig.direction === "desc") {
      // Reset sort when clicking the same column for the third time
      setSortConfig({ key: null, direction: "asc" });
      setCurrentPage(1);
      return;
    }
    setSortConfig({ key: columnKey, direction });
    setCurrentPage(1);
  };

  const handlePreviewSelect = (preview) => {
    setIsPreviewModalOpen(false);
    if (preview) {
      if (project) {
        // For ProjectDetail - link preview only (not full project update)
        setIsUpdatingProject(true);
        updateProjectPreviewLink.mutate({
          projectPreviewSeq: preview.projectPreviewSeq,
        });
      }
      // For both cases - update local state
      setSelectedPreview(preview);
      setSelectedLisencePolicySeq(preview.licensePolicySeq);
      setCurrentPage(1);
      setSortConfig({ key: null, direction: "asc" });
      setIsExpanded(true);
    }
  };

  const handleDeleteComponents = () => {
    if (onDeleteComponents) {
      onDeleteComponents(selectedRows);
      // Reset selected rows after deletion
      setSelectedRows([]);
    } else {
    }
  };

  const handleRowSelection = (selectedRowKeys) => {
    setSelectedRows(selectedRowKeys);
  };

  const handleRemoveSelectedComponent = () => {
    if (onRemoveSelectedComponent) {
      onRemoveSelectedComponent();
    } else if (project) {
      // Default behavior for ProjectDetail - clear linked preview only
      setIsUpdatingProject(true);
      updateProjectPreviewLink.mutate({ projectPreviewSeq: null });
      setSelectedPreview(null);
      setSelectedLisencePolicySeq(null);
      setPreviewComponents([]);
      setTotalData(0);
      setTotalPages(0);
      setCurrentPage(1);
      setSortConfig({ key: null, direction: "asc" });
    }
    // For PreviewDetail, do nothing (no project to update)
  };

  const columns = showCheckbox
    ? ["conflictStatus", "component", "cpe"]
    : ["conflictStatus", "componentName", "componentVersion", "licenseIdentifier", "cpe"];

  const createColorGrid = useCallback(
    (row) => {
      let licensePolicyResult = getLicensePolicyResult(licensePolicy, row.licenseAttribute);
      let policyConflictColor = licensePolicyResult.policyConflictColor;
      let licensePolicyRule = getLicensePolicyRule(licensePolicy.licensePolicyRules, row);

      if (licensePolicyRule != null) {
        policyConflictColor = COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;
        if (licensePolicyRule.licenseUseApprovalCd == "DISAPPROVAL") {
          policyConflictColor = COLOR_CONFLICT_FLAG.POLICY_CONFLICT;
        }
      }

      row.licensePolicyResult = licensePolicyResult;
      let componentConflictResult = getComponentLicenseConflict(previewComponents, row);
      row.componentConflictResult = componentConflictResult;

      const itemStyle = {
        border: "none",
      };

      const policyConflict = {
        ...itemStyle,
        backgroundColor: policyConflictColor,
      };

      const componentConflict = {
        ...itemStyle,
        backgroundColor: componentConflictResult.conflict ? COLOR_CONFLICT_FLAG.LICENSE_CONFLICT : COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT,
      };

      return { policyConflict, componentConflict };
    },
    [licensePolicy, previewComponents],
  );

  const columnConfig = showCheckbox
    ? {
        conflictStatus: {
          title: "CONFLICT STATUS",
          style: {
            width: "15%",
          },
          headerRender: () => t("project.conflict-status"),
          customRender: (value, row) => renderConflictStatus(value, row),
        },
        component: {
          title: "COMPONENT",
          style: {
            width: "45%",
          },
          headerRender: () => <ComponentHeader/>,
          customRender: (value, row) => {
            const licenseIdentifier = row.licenseIdentifier;
            const isValidLicense = licenseIdentifier != null && String(licenseIdentifier).toUpperCase() !== "N/A";
            const licenseText = isValidLicense ? licenseIdentifier : "";

            return (
            <div className="flex flex-col">
              <span className={`text-sm ${colors.textPrimary}`}>
                {row.componentName} ({row.componentVersion})
              </span>
              <span className={`text-xs ${colors.textSecondary}`}>
                <span className={licenseText ? "" : "invisible"}>{licenseText || "-"}</span>
              </span>
            </div>
            );
          },
        },
        cpe: {
          title: "CPE (COMMON PLATFORM ENUMERATION)",
          style: {
            width: "40%",
          },
          headerRender: () => t("open-source.detail-cpe"),
          customRender: (value, row) => <span className={`text-sm ${colors.textPrimary}`}>{row.cpe || "---"}</span>,
        },
      }
    : {
        conflictStatus: {
          title: "CONFLICT STATUS",
          style: {
            width: "10%",
          },
          headerRender: () => t("project.conflict-status"),
          customRender: (value, row) => renderConflictStatus(value, row),
        },
        componentName: {
          title: "COMPONENT NAME",
          style: {
            width: "18%",
          },
          headerRender: () => t("open-source.component-name"),
        },
        componentVersion: {
          title: "COMP. VERSION",
          headerRender: () => t("common.component-version-column"),
        },
        licenseIdentifier: {
          title: "COMPONENT LICENSE",
          headerRender: () => t("common.component-license"),
        },
        cpe: {
          title: "CPE (COMMON PLATFORM ENUMERATION)",
          headerRender: () => t("open-source.detail-cpe"),
        },
      };

  const renderConflictStatus = (value, row) => {
    const { policyConflict, componentConflict } = createColorGrid(row);
    const conflictRowId = row?.projectPreviewComponentSeq ?? row?.componentSeq ?? row?.componentName ?? "unknown";

    const handleLicenseConflictClick = () => {
      if (componentConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT && row.componentConflictResult?.conflictComponents) {
        setSelectedConflictData(row.componentConflictResult.conflictComponents);
        setIsLicenseConflictModalOpen(true);
      }
    };

    return (
      <div className="flex items-center justify-start w-full" data-testid={tid(`conflict-status-${conflictRowId}`)}>
        <div className="flex items-center gap-x-0.5" data-testid={tid(`conflict-status-${conflictRowId}-container`)}>
          <div
            className={`w-8 h-8 flex items-center justify-center text-base font-bold rounded-l-sm ${policyConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
            style={{ backgroundColor: policyConflict.backgroundColor, borderRight: "none" }}
            data-testid={tid(`conflict-status-${conflictRowId}-policy`)}
            onClick={() => {
              if (policyConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT) {
                setSelectedLicensePolicyResult(row.licensePolicyResult);
                setIsPolicyConflictModalOpen(true);
              }
            }}
          >
            <span className={policyConflict.backgroundColor === COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT ? colors.textConflictUndefined : colors.textNormal}>P</span>
          </div>
          <div
            className={`w-8 h-8 flex items-center justify-center text-base font-bold rounded-r-sm transition-opacity ${
              componentConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT &&
              row.componentConflictResult?.conflictComponents
                ? "cursor-pointer hover:opacity-80"
                : "cursor-default"
            }`}
            style={{ backgroundColor: componentConflict.backgroundColor, borderLeft: "none" }}
            data-testid={tid(`conflict-status-${conflictRowId}-license`)}
            onClick={
              componentConflict.backgroundColor !== COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT &&
              row.componentConflictResult?.conflictComponents
                ? handleLicenseConflictClick
                : undefined
            }
          >
            <span className={componentConflict.backgroundColor === COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT ? colors.textConflictUndefined : colors.textNormal}>L</span>
          </div>
        </div>
      </div>
    );
  };

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  const sortedData = React.useMemo(() => {
    // If sort is disabled, return previewComponents directly
    if (!sortConfig.key || !previewComponents.length) {
      return previewComponents;
    }
    return [...previewComponents].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined values - always put them at the end
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1; // null values always go to the end
      if (bValue == null) return -1; // null values always go to the end

      // Convert to strings for comparison
      const aStr = String(aValue).toLowerCase().trim();
      const bStr = String(bValue).toLowerCase().trim();

      // Try numeric comparison first
      const aNum = parseFloat(aStr);
      const bNum = parseFloat(bStr);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        const comparison = aNum - bNum;
        return sortConfig.direction === "asc" ? comparison : -comparison;
      }

      // Try date comparison
      const aDate = new Date(aStr);
      const bDate = new Date(bStr);
      if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
        const comparison = aDate.getTime() - bDate.getTime();
        return sortConfig.direction === "asc" ? comparison : -comparison;
      }

      // Default to string comparison
      const comparison = aStr.localeCompare(bStr);
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [previewComponents, sortConfig]);

  return (
    <div
      data-testid={dataTestId}
      className={`rounded-2xl px-6 pt-6 pb-4 flex flex-col ${isExpanded && (previewComponents.length === 0 || !selectedPreview?.projectPreviewSeq) ? "min-h-[19rem]" : ""} ${colors.bgPrimary} ${colors.borderPrimary} ${className}`}
    >
      <div
        data-testid={tid("toggle")}
        className={`mb-2 flex cursor-pointer items-center justify-between`}
        tabIndex={0}
        role="button"
        aria-label={isExpanded ? "Collapse Preview Section" : "Expand Preview Section"}
        onClick={toggleAccordion}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleAccordion();
          }
        }}
      >
        <div className="flex items-center" data-testid={tid("header-left")}>
          <ChevronUpIcon
            className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${!isExpanded ? "rotate-180" : ""}`}
            data-testid={tid("chevron")}
          />
          <h2 className={`text-md font-semibold ${colors.textPrimary}`} data-testid={tid("title")}>
            {title}
          </h2>
        </div>
        {
          <div onClick={(e) => e.stopPropagation()} data-testid={tid("actions")}>
            {showActionButtons && (
              <div className="flex space-x-3" data-testid={tid("action-buttons")}>
                <DeleteButton
                  onClick={handleDeleteComponents}
                  disabled={
                    showCheckbox
                      ? selectedRows.length === 0 || isUpdatingProject
                      : previewComponents.length === 0 || isUpdatingProject
                  }
                  size="md"
                  data-testid={tid("delete-components-button")}
                >
                  {t("common.delete-components")}
                </DeleteButton>
                <PrimaryButton
                  onClick={() => (onAddComponents ? onAddComponents() : setIsPreviewModalOpen(true))}
                  disabled={isUpdatingProject}
                  size="md"
                  data-testid={tid("add-components-button")}
                >
                  {t("common.add-components")}
                </PrimaryButton>
              </div>
            )}
            {!showActionButtons && isPreviewEnabled && (
              <OutlineButton
                onClick={() => setIsPreviewModalOpen(true)}
                disabled={isUpdatingProject}
                size="lg"
                data-testid={tid("select-preview-button")}
              >
                {isUpdatingProject ? t("common.updating") : t("project.select-preview-title")}
              </OutlineButton>
            )}
          </div>
        }
      </div>
      {isExpanded && (
        <>
          {showSelectedComponent && selectedPreview?.projectPreviewName && (
            <div className="flex items-center w-full mb-4" data-testid={tid("selected-preview")}>
              <span className={`text-sm ${colors.textSecondary} mr-4 whitespace-nowrap`} data-testid={tid("selected-preview-label")}>
                {t("project.project-preview.selected-component")}
              </span>
              <div className={`flex items-center rounded-lg px-3 h-10 w-90 ${colors.bgSelectedPreviewName}`} data-testid={tid("selected-preview-name-container")}>
                <span
                  title={selectedPreview.projectPreviewName}
                  className={`text-sm font-medium ${colors.textPrimary} truncate flex-1`}
                  data-testid={tid("selected-preview-name")}
                >
                  {selectedPreview.projectPreviewName}
                </span>
                {isPreviewEnabled && (
                  <button
                    type="button"
                    data-testid={tid("remove-selected-button")}
                    className={`ml-2 p-1 ${colors.textMuted} ${colors.hoverTextDanger} flex-shrink-0 flex items-center justify-center`}
                    onClick={handleRemoveSelectedComponent}
                    disabled={isUpdatingProject}
                  >
                    <TrashIcon className="h-4 w-4" data-testid={tid("remove-selected-icon")} />
                  </button>
                )}
              </div>
            </div>
          )}

          <div
            data-testid={tid("content")}
            className={`pt-1 ${previewComponents.length === 0 && !loading ? "flex-1 flex items-center justify-center" : ""}`}
          >
            {(previewComponents.length === 0 && !loading) || !selectedPreview?.projectPreviewName ? (
              <div className="flex flex-col items-center justify-center" data-testid={tid("empty-state")}>
                <div className="mb-4" data-testid={tid("empty-state-icon-wrapper")}>
                  <DropIcon className={`h-20 w-20 ${colors.textActive}`} data-testid={tid("empty-state-icon")} />
                </div>
                <p className={`${colors.textSecondary} text-sm`} data-testid={tid("empty-state-text")}>
                  {t("project.project-preview.select-preview-component-to-view-details")}
                </p>
              </div>
            ) : (
              <TableWithPagination
                columns={columns}
                data={sortedData}
                columnConfig={columnConfig}
                loading={loading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                totalData={totalData}
                maxRowsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChangeWithReset}
                rowKey="projectPreviewComponentSeq"
                showPagination={true}
                shouldShowGoToPage={true}
                hasCheckbox={showCheckbox}
                selectedRows={selectedRows}
                onCheckboxChange={handleRowSelection}
                showItemsPerPageSelector={true}
                dataTestId={tid("table")}
              />
            )}
          </div>
        </>
      )}

      {/* Preview Selection Modal */}
      <PreviewSelectionModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        onSelect={handlePreviewSelect}
        selectedPreview={selectedPreview}
        creatorList={creatorList}
        data-testid={tid("preview-selection-modal")}
      />

      {/* Policy Conflict Modal */}
      <PolicyConflictModal
        isOpen={isPolicyConflictModalOpen}
        onClose={() => {
          setIsPolicyConflictModalOpen(false);
          setSelectedLicensePolicyResult(null);
        }}
        licensePolicyResult={selectedLicensePolicyResult}
        data-testid={tid("policy-conflict-modal")}
      />

      {/* License Conflict Modal */}
      <LicenseConflictModal
        isOpen={isLicenseConflictModalOpen}
        onClose={() => setIsLicenseConflictModalOpen(false)}
        conflictData={selectedConflictData}
        data-testid={tid("license-conflict-modal")}
      />
    </div>
  );
};

export default PreviewSection;
